import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Zap, Loader2, RefreshCw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

/**
 * Document 2 · Phase 6.4 — Build & Send Telemetry Tile.
 *
 * Read-only Dashboard KPI tile aggregating the Build & Send funnel for the
 * current user (or workspace if admin). Pure consumer of
 * `GET /api/build-and-send/telemetry?window=...` — no schema awareness.
 *
 * Each conversion percentage is color-coded (green ≥ 60%, amber 30-60%, red < 30%)
 * so a manager can read the tile at a glance.
 */

const WINDOW_OPTIONS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
];

function pctTone(pct) {
  if (pct >= 60) {
    return { ring: 'border-emerald-300 bg-emerald-50 text-emerald-800', dot: 'bg-emerald-500', label: 'On track' };
  }
  if (pct >= 30) {
    return { ring: 'border-amber-300 bg-amber-50 text-amber-800', dot: 'bg-amber-500', label: 'Watch' };
  }
  return { ring: 'border-red-300 bg-red-50 text-red-800', dot: 'bg-red-500', label: 'Below target' };
}

function StatPill({ label, count, started, pct, testid }) {
  const tone = pctTone(pct);
  return (
    <div
      className={`rounded-sm border px-3 py-2 ${tone.ring}`}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
          {label}
        </p>
        <span
          className={`inline-block h-2 w-2 rounded-full ${tone.dot}`}
          aria-label={tone.label}
          title={tone.label}
          data-testid={`${testid}-tone-dot`}
        />
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className="text-xl font-semibold tabular-nums" data-testid={`${testid}-count`}>
          {count}
          <span className="text-xs font-normal opacity-70">/{started || 0}</span>
        </span>
        <span
          className="text-xs font-semibold tabular-nums"
          data-testid={`${testid}-pct`}
        >
          {pct}%
        </span>
      </div>
    </div>
  );
}

export default function BuildAndSendTelemetryTile() {
  const api = useApi();
  const [window, setWindow] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTelemetry = useCallback(
    async (w) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/build-and-send/telemetry?window=${w}`);
        setData(res);
      } catch (err) {
        setError(err?.message || 'Failed to load telemetry');
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    fetchTelemetry(window);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const started = data?.started ?? 0;
  const empty = !loading && !error && started === 0;

  return (
    <Card
      className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:border-amber-300/60 hover:shadow-md transition-all duration-300"
      data-testid="bs-telemetry-tile"
    >
      <CardContent className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-sm bg-amber-50 text-amber-700 flex-shrink-0">
              <Zap className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                Build &amp; Send
              </p>
              <h3 className="text-sm font-semibold text-stone-900 truncate">
                Conversion · last {window}
              </h3>
            </div>
          </div>
          <div
            className="flex items-center rounded-sm border border-stone-200 bg-stone-50 p-0.5"
            data-testid="bs-telemetry-window-switcher"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setWindow(opt.id)}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm transition-colors ${
                  window === opt.id
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                data-testid={`bs-telemetry-window-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {loading && (
          <div className="flex items-center justify-center py-6 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">Loading…</span>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-4">
            <p className="text-xs text-red-700 mb-2">{error}</p>
            <button
              type="button"
              onClick={() => fetchTelemetry(window)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-600 hover:text-stone-900"
              data-testid="bs-telemetry-retry"
            >
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          </div>
        )}

        {!loading && !error && empty && (
          <div className="py-5 text-center text-xs text-stone-500" data-testid="bs-telemetry-empty">
            No Build &amp; Send workflows in the last {window}.
            <p className="mt-1 text-[11px] text-stone-400">
              Run one from any opportunity to start tracking conversion.
            </p>
          </div>
        )}

        {!loading && !error && !empty && (
          <>
            {/* Big number */}
            <div className="flex items-baseline gap-2 mb-3">
              <span
                className="text-3xl font-light text-stone-900 tabular-nums"
                data-testid="bs-telemetry-started"
              >
                {started}
              </span>
              <span className="text-xs text-stone-500">workflows started</span>
              {data?.scope === 'workspace' && (
                <Badge
                  variant="outline"
                  className="ml-auto text-[9px] uppercase tracking-wider font-semibold border-stone-300 text-stone-600"
                >
                  workspace
                </Badge>
              )}
            </div>

            {/* Three conversion pills */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <StatPill
                label="Deck published"
                count={data.deck_published}
                started={started}
                pct={data.deck_published_pct}
                testid="bs-telemetry-published"
              />
              <StatPill
                label="Any email sent"
                count={data.email_any_sent}
                started={started}
                pct={data.email_any_sent_pct}
                testid="bs-telemetry-any-sent"
              />
              <StatPill
                label="Both versions sent"
                count={data.both_sent}
                started={started}
                pct={data.both_sent_pct}
                testid="bs-telemetry-both-sent"
              />
            </div>

            {/* Footer */}
            {data.avg_hours_to_first_send !== null && data.avg_hours_to_first_send !== undefined && (
              <p
                className="mt-3 text-[11px] text-stone-500 inline-flex items-center gap-1"
                data-testid="bs-telemetry-avg-time"
              >
                <span className="inline-block h-1 w-1 rounded-full bg-stone-400" />
                Avg time to first send: <span className="font-semibold text-stone-700">{data.avg_hours_to_first_send}h</span>
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
